import { create } from 'zustand';
import { useAuthStore } from './useAuthStore';

interface Location {
  lat: number;
  lng: number;
  address?: string;
}

interface SavedMarker extends Location {
  id: string;
  comment: string;
  timestamp: string;
  locationType: string;
  unitNumber?: string;
  userEmail: string;
  commentId: string;
}

interface MapData {
  center: Location;
  zoom: number;
  activeMarker: Location | null; // Only one active marker at a time
  savedMarkers: SavedMarker[]; // Saved markers with comments
  selectedLocation: Location | null;
}

interface MapState {
  mapData: MapData;
  setCenter: (location: Location) => void;
  setZoom: (zoom: number) => void;
  setActiveMarker: (location: Location | null) => void;
  saveMarker: (data: { comment: string; locationType: string; unitNumber?: string }) => void;
  removeSavedMarker: (id: string) => void;
  updateComment: (commentId: string, newComment: string) => void;
  updateActiveMarkerPosition: (location: Location) => void;
  setSelectedLocation: (location: Location | null) => void;
  clearAllMarkers: () => void;
}

const defaultMapData: MapData = {
  center: { lat: -33.8688, lng: 151.2093 }, // Sydney coordinates
  zoom: 14,
  activeMarker: null,
  savedMarkers: [],
  selectedLocation: null,
};

// Generate a unique ID for comments
const generateCommentId = (): string => {
  return `comment_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

export const useMapStore = create<MapState>((set) => ({
  mapData: defaultMapData,
  
  setCenter: (location: Location) =>
    set((state) => ({
      mapData: { ...state.mapData, center: location }
    })),
    
  setZoom: (zoom: number) =>
    set((state) => ({
      mapData: { ...state.mapData, zoom }
    })),
    
  setActiveMarker: (location: Location | null) =>
    set((state) => ({
      mapData: { 
        ...state.mapData, 
        activeMarker: location 
      }
    })),
    
  saveMarker: ({ comment, locationType, unitNumber }) =>
    set((state) => {
      console.log('saveMarker called with:', { comment, locationType, unitNumber });
      console.log('Current state - activeMarker:', state.mapData.activeMarker);
      console.log('Current state - selectedLocation:', state.mapData.selectedLocation);
      
      // Get current user from auth store
      const currentUser = useAuthStore.getState().user;
      if (!currentUser) {
        console.error('No authenticated user found');
        return state;
      }
      
      // Use activeMarker first, fall back to selectedLocation if activeMarker is null
      const locationToSave = state.mapData.activeMarker || state.mapData.selectedLocation;
      
      console.log('Location to save:', locationToSave);
      console.log('Current user:', currentUser);
      
      if (!locationToSave) {
        console.log('No location to save - returning current state');
        return state;
      }
      
      const savedMarker: SavedMarker = {
        ...locationToSave,
        id: Date.now().toString(),
        commentId: generateCommentId(),
        comment,
        locationType,
        unitNumber,
        timestamp: new Date().toISOString(),
        userEmail: currentUser.email,
      };
      
      console.log('Created saved marker:', savedMarker);
      
      return {
        mapData: {
          ...state.mapData,
          activeMarker: null, // Clear active marker after saving
          selectedLocation: null, // Clear selected location after saving
          savedMarkers: [...state.mapData.savedMarkers, savedMarker]
        }
      };
    }),
    
  removeSavedMarker: (id: string) =>
    set((state) => ({
      mapData: {
        ...state.mapData,
        savedMarkers: state.mapData.savedMarkers.filter(marker => marker.id !== id)
      }
    })),
    
  updateComment: (commentId: string, newComment: string) =>
    set((state) => ({
      mapData: {
        ...state.mapData,
        savedMarkers: state.mapData.savedMarkers.map(marker =>
          marker.commentId === commentId ? { ...marker, comment: newComment } : marker
        )
      }
    })),
    
  updateActiveMarkerPosition: (location: Location) =>
    set((state) => ({
      mapData: {
        ...state.mapData,
        activeMarker: location
      }
    })),
    
  setSelectedLocation: (location: Location | null) =>
    set((state) => ({
      mapData: { ...state.mapData, selectedLocation: location }
    })),
    
  clearAllMarkers: () =>
    set((state) => ({
      mapData: { 
        ...state.mapData, 
        activeMarker: null,
        savedMarkers: [] 
      }
    })),
})); 